import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/Common/button';
import Modal from '@/components/Common/Modal';
import { useCreateEvaluation, EvaluationResponse } from '@/services/evaluations';
import notify from '@/lib/notify';

interface EvaluationButtonsProps {
  messageId: string;
  conversationId: string;
  question: string;
  response: string;
  citations: any[];
  existingEvaluation?: EvaluationResponse | null;
}

export const EvaluationButtons: React.FC<EvaluationButtonsProps> = ({
  messageId,
  conversationId,
  question,
  response,
  citations,
  existingEvaluation
}) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const createEvaluation = useCreateEvaluation();
  const hasPersistedIds = !messageId.startsWith('temp-') && !conversationId.startsWith('temp-');

  // Synchroniser l'état avec la base de données
  useEffect(() => {
    if (existingEvaluation) {
      setFeedback(existingEvaluation.feedback as 'positive' | 'negative');
    }
  }, [existingEvaluation]);

  const handlePositive = async () => {
    if (!hasPersistedIds) {
      notify.error(t('chatPage.evaluation.stillSaving'));
      return;
    }

    try {
      await createEvaluation.mutateAsync({
        conversation_id: conversationId,
        message_id: messageId,
        feedback: 'positive',
        comment: null,
        question,
        response,
        citations
      });

      setFeedback('positive');
      notify.success(t('chatPage.evaluation.thanks'));
    } catch (error) {
      notify.error(t('chatPage.evaluation.sendError'));
    }
  };

  const handleNegative = () => {
    setIsModalOpen(true);
  };

  const handleSubmitNegative = async () => {
    if (!hasPersistedIds) {
      notify.error(t('chatPage.evaluation.stillSaving'));
      return;
    }

    if (!comment.trim()) {
      notify.error(t('chatPage.evaluation.commentRequired'));
      return;
    }

    try {
      await createEvaluation.mutateAsync({
        conversation_id: conversationId,
        message_id: messageId,
        feedback: 'negative',
        comment,
        question,
        response,
        citations
      });

      setFeedback('negative');
      setIsModalOpen(false);
      setComment('');
      notify.success(t('chatPage.evaluation.thanks'));
    } catch (error) {
      notify.error(t('chatPage.evaluation.sendError'));
    }
  };

  if (feedback) {
    return (
      <div className="text-xs text-muted-foreground">
        {t('chatPage.evaluation.sent')} {feedback === 'positive' ? '👍' : '👎'}
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={handlePositive}
          disabled={createEvaluation.isPending || !hasPersistedIds}
        >
          <ThumbsUp className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleNegative}
          disabled={createEvaluation.isPending || !hasPersistedIds}
        >
          <ThumbsDown className="h-4 w-4" />
        </Button>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('chatPage.evaluation.modalTitle')}
        hideDefaultFooter
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('chatPage.evaluation.modalDescription')}
          </p>
          <textarea
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
            placeholder={t('chatPage.evaluation.commentPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              {t('global.cancel')}
            </Button>
            <Button onClick={handleSubmitNegative} disabled={!comment.trim()}>
              {t('global.send')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
